import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  Popover,
  StepLabel,
  alpha,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  endOfDay,
  format,
  isBefore,
  isSameDay,
  isWithinInterval,
  startOfDay,
} from "date-fns";
import React, { useMemo, useState } from "react";
import { COLORS } from "../../assets/theme";

interface CustomDateRangePickerProps {
  fromDate?: Date | null;
  toDate?: Date | null;
  setFromDate?: React.Dispatch<React.SetStateAction<Date | null>>;
  setToDate?: React.Dispatch<React.SetStateAction<Date | null>>;
  label?: string;
  fullWidth?: boolean;
  width?: string;
  minWidth?: string;
}

const toUTCDateTime = (date: Date | null): Date | null =>
  date
    ? new Date(
        Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          date.getHours(),
          date.getMinutes(),
          date.getSeconds(),
          date.getMilliseconds(),
        ),
      )
    : null;

const toLocalDateTime = (utcDate: Date | null): Date | null =>
  utcDate
    ? new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000)
    : null;

const mergeDateAndTime = (date: Date, time: Date): Date =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    0,
    0,
  );

const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  label = "Date Range",
  fullWidth,
  width,
  minWidth,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const localFromDate = useMemo(
    () => toLocalDateTime(fromDate ?? null),
    [fromDate],
  );
  const localToDate = useMemo(() => toLocalDateTime(toDate ?? null), [toDate]);

  const open = Boolean(anchorEl);
  const controlSx = fullWidth
    ? { width: "100%" }
    : {
        width: { xs: "100%", sm: width ?? "25%" },
        maxWidth: "100%",
        minWidth: { xs: 0, sm: minWidth ?? "360px" },
      };

  const rangeDisplay = useMemo(() => {
    if (localFromDate && localToDate) {
      return {
        from: format(localFromDate, "yyyy-MM-dd HH:mm"),
        to: format(localToDate, "yyyy-MM-dd HH:mm"),
      };
    }

    if (localFromDate) {
      return {
        from: format(localFromDate, "yyyy-MM-dd HH:mm"),
        to: "Select end date",
      };
    }

    return null;
  }, [localFromDate, localToDate]);

  const handleDateSelect = (selectedDate: Date | null) => {
    if (!selectedDate || !setFromDate || !setToDate) return;

    const selectedDayStart = startOfDay(selectedDate);

    if (
      !localFromDate ||
      localToDate ||
      isBefore(selectedDayStart, startOfDay(localFromDate))
    ) {
      setFromDate(
        toUTCDateTime(
          mergeDateAndTime(selectedDayStart, localFromDate ?? selectedDayStart),
        ),
      );
      setToDate(null);
      return;
    }

    setToDate(
      toUTCDateTime(
        mergeDateAndTime(selectedDate, localToDate ?? endOfDay(selectedDate)),
      ),
    );
  };

  const handleClear = () => {
    setFromDate?.(null);
    setToDate?.(null);
  };

  const timePickerTextFieldProps = {
    size: "small" as const,
    fullWidth: true,
    sx: {
      "& .MuiInputLabel-root.Mui-focused": {
        color: COLORS.GOLD,
      },
      "& .MuiOutlinedInput-root": {
        height: 40,
        borderRadius: "12px",
        fontSize: "12px",
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: COLORS.GOLD,
        },
      },
    },
  };

  const timePickerSlotProps = {
    textField: timePickerTextFieldProps,
    popper: {
      sx: {
        "& .MuiClock-pin, & .MuiClockPointer-root, & .MuiClockPointer-thumb": {
          backgroundColor: COLORS.GOLD,
        },
        "& .MuiClockPointer-thumb": {
          borderColor: COLORS.GOLD,
        },
        "& .MuiClockNumber-root.Mui-selected, & .MuiMenuItem-root.Mui-selected":
          {
            color: COLORS.iconColor,
            backgroundColor: COLORS.GOLD,
          },
        "& .MuiMenuItem-root.Mui-selected:hover": {
          backgroundColor: COLORS.GOLD,
        },
      },
    },
    desktopPaper: {
      sx: {
        "& .MuiClock-pin, & .MuiClockPointer-root, & .MuiClockPointer-thumb": {
          backgroundColor: COLORS.GOLD,
        },
        "& .MuiClockPointer-thumb": {
          borderColor: COLORS.GOLD,
        },
        "& .MuiClockNumber-root.Mui-selected, & .MuiMenuItem-root.Mui-selected":
          {
            color: COLORS.iconColor,
            backgroundColor: COLORS.GOLD,
          },
        "& .MuiMenuItem-root.Mui-selected:hover": {
          backgroundColor: COLORS.GOLD,
        },
      },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormControl variant="outlined" sx={controlSx}>
        <StepLabel>{label}</StepLabel>
        <Box
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr) auto",
            alignItems: "center",
            columnGap: 1,
            height: "42px",
            width: "100%",
            padding: "6px 12px",
            border: "1px solid rgba(0, 0, 0, 0.23)",
            borderRadius: "12px",
            cursor: "pointer",
            fontSize: "14px",
            boxSizing: "border-box",
            "&:hover": {
              borderColor: "#868585",
            },
          }}
        >
          {rangeDisplay ? (
            <>
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  justifySelf: "center",
                  textAlign: "center",
                }}
              >
                {rangeDisplay.from}
              </Box>
              <Box component="span" sx={{ justifySelf: "center" }}>
                -
              </Box>
              <Box
                component="span"
                sx={{
                  justifySelf: "center",
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {rangeDisplay.to}
              </Box>
            </>
          ) : (
            <Box
              component="span"
              sx={{
                gridColumn: "1 / 4",
                color: COLORS.placeHolderColor,
              }}
            >
              {label}
            </Box>
          )}
          <InputAdornment position="end" sx={{ m: 0 }}>
            <CalendarTodayIcon fontSize="small" />
          </InputAdornment>
        </Box>
      </FormControl>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box p={1}>
          <DateCalendar
            value={localToDate ?? localFromDate}
            onChange={handleDateSelect}
            slotProps={{
              day: (ownerState: any) => {
                const day = ownerState.day as Date;
                const isStart = localFromDate
                  ? isSameDay(day, localFromDate)
                  : false;
                const isEnd = localToDate ? isSameDay(day, localToDate) : false;
                const isBetween =
                  localFromDate && localToDate
                    ? isWithinInterval(day, {
                        start: startOfDay(localFromDate),
                        end: endOfDay(localToDate),
                      })
                    : false;
                const isSingleDayRange =
                  localFromDate && localToDate
                    ? isSameDay(localFromDate, localToDate)
                    : false;

                return {
                  selected: isStart || isEnd,
                  sx: {
                    ...(isBetween && {
                      borderRadius: 0,
                      backgroundColor: alpha(COLORS.GOLD, 0.2),
                      "&:hover, &:focus": {
                        backgroundColor: alpha(COLORS.GOLD, 0.32),
                      },
                    }),
                    "&.Mui-selected": {
                      color: COLORS.iconColor,
                      backgroundColor: COLORS.GOLD,
                      "&:hover, &:focus": {
                        backgroundColor: COLORS.GOLD,
                      },
                    },
                    "&.MuiPickersDay-today": {
                      borderColor: COLORS.GOLD,
                    },
                    ...(isStart &&
                      !isSingleDayRange && {
                        borderTopLeftRadius: "50%",
                        borderBottomLeftRadius: "50%",
                      }),
                    ...(isEnd &&
                      !isSingleDayRange && {
                        borderTopRightRadius: "50%",
                        borderBottomRightRadius: "50%",
                      }),
                  },
                };
              },
            }}
          />
          <Box display="flex" gap={1.5} px={2} pb={1}>
            <TimePicker
              label="From Time"
              value={localFromDate}
              onChange={(time) => {
                if (!time || !localFromDate) return;
                setFromDate?.(
                  toUTCDateTime(mergeDateAndTime(localFromDate, time)),
                );
              }}
              disabled={!localFromDate}
              ampm={false}
              slotProps={timePickerSlotProps}
            />
            <TimePicker
              label="To Time"
              value={localToDate}
              onChange={(time) => {
                if (!time || !localToDate) return;
                setToDate?.(toUTCDateTime(mergeDateAndTime(localToDate, time)));
              }}
              disabled={!localToDate}
              ampm={false}
              slotProps={timePickerSlotProps}
            />
          </Box>
          <Box display="flex" justifyContent="space-between" px={2} pb={1}>
            <Button
              size="small"
              onClick={handleClear}
              sx={{
                color: COLORS.GOLD,
              }}
            >
              Clear
            </Button>
            <Box display="flex" gap={1}>
              {localFromDate && !localToDate && (
                <Button
                  size="small"
                  onClick={() => {
                    setToDate?.(toUTCDateTime(endOfDay(localFromDate)));
                  }}
                  sx={{
                    color: COLORS.GOLD,
                  }}
                >
                  Same day
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                sx={{
                  backgroundColor: COLORS.GOLD,
                  color: COLORS.iconColor,
                  "&:hover": {
                    backgroundColor: COLORS.GOLD,
                  },
                }}
                onClick={() => setAnchorEl(null)}
              >
                Done
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default CustomDateRangePicker;
